import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Mail, MicOff, PhoneIncoming, PhoneOutgoing, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDuration, fmtPhone, fmtShort } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, FilterSelect, Loading, PageHeader, Pill, RangePicker, ScoreBadge, sentimentTone, useRange } from "@/components/common";
import CallDrawer from "@/components/CallDrawer";

const EMPTY = { agent: "", direction: "", sentiment: "", call_type: "", analysis: "", min_score: "", max_score: "", q: "" };

export default function Calls() {
  const { isAdmin } = useAuth();
  const [range, setRange] = useRange(30);
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [qInput, setQInput] = useState("");
  const [params, setParams] = useSearchParams();
  const openId = params.get("call");

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: qInput })), 350);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => setPage(1), [filters, range]);

  const query = useMemo(() => {
    const p = { date_from: range.from, date_to: range.to, page, limit: 25 };
    Object.entries(filters).forEach(([k, v]) => v !== "" && (p[k] = v));
    return p;
  }, [filters, range, page]);

  const calls = useQuery({ queryKey: ["calls", query], queryFn: () => api.get("/calls", { params: query }).then((r) => r.data), placeholderData: keepPreviousData });
  const meta = useQuery({ queryKey: ["call-filters"], queryFn: () => api.get("/calls/filters").then((r) => r.data) });

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const active = Object.values(filters).filter((v) => v !== "").length;
  const pages = Math.max(1, Math.ceil((calls.data?.total || 0) / 25));

  return (
    <>
      <PageHeader eyebrow="Call intelligence" title={isAdmin ? "Calls & AI coaching" : "My calls"} description="Every OpenPhone call, searchable and filterable. Open a call for the transcript, score breakdown and the coaching email that was sent." actions={<RangePicker range={range} onChange={setRange} />} testId="calls-header" />

      <div className="panel p-3 flex flex-wrap items-center gap-2 fade-up" data-testid="calls-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input data-testid="calls-search-input" placeholder="Search number, agent, topic, summary or transcript…" value={qInput} onChange={(e) => setQInput(e.target.value)} className="h-8 pl-8 text-xs bg-[#0f172a] border-[#1f293d]" />
        </div>
        {isAdmin && <FilterSelect testId="calls-filter-agent" placeholder="Agents" value={filters.agent} onChange={set("agent")} options={(meta.data?.agents || []).map((a) => ({ value: a.id, label: `${a.name} (${a.calls})` }))} />}
        <FilterSelect testId="calls-filter-direction" placeholder="Directions" value={filters.direction} onChange={set("direction")} options={[{ value: "incoming", label: "Inbound" }, { value: "outgoing", label: "Outbound" }]} />
        <FilterSelect testId="calls-filter-sentiment" placeholder="Sentiments" value={filters.sentiment} onChange={set("sentiment")} options={["positive", "neutral", "negative"].map((s) => ({ value: s, label: s }))} />
        <FilterSelect testId="calls-filter-type" placeholder="Types" value={filters.call_type} onChange={set("call_type")} options={(meta.data?.call_types || []).map((t) => ({ value: t, label: t }))} />
        <FilterSelect
          testId="calls-filter-analysis"
          placeholder="Statuses"
          value={filters.analysis}
          onChange={set("analysis")}
          options={[
            { value: "analyzed", label: "AI reviewed" },
            { value: "below_threshold", label: "Below threshold" },
            { value: "pending", label: "Awaiting transcript" },
            { value: "no_transcript", label: "No transcript" },
            { value: "failed", label: "Analysis failed" },
            { value: "unrecorded", label: "Not recorded" },
          ]}
        />
        <div className="flex items-center gap-1">
          <Input data-testid="calls-filter-min-score" type="number" min={0} max={100} placeholder="min" value={filters.min_score} onChange={(e) => set("min_score")(e.target.value)} className="h-8 w-16 text-xs font-mono bg-[#0f172a] border-[#1f293d]" />
          <span className="text-slate-600 text-xs">–</span>
          <Input data-testid="calls-filter-max-score" type="number" min={0} max={100} placeholder="max" value={filters.max_score} onChange={(e) => set("max_score")(e.target.value)} className="h-8 w-16 text-xs font-mono bg-[#0f172a] border-[#1f293d]" />
        </div>
        {active > 0 && (
          <Button data-testid="calls-clear-filters" variant="ghost" size="sm" className="h-8 text-xs text-slate-400" onClick={() => { setFilters(EMPTY); setQInput(""); }}>
            <X className="h-3.5 w-3.5 mr-1" /> Clear {active}
          </Button>
        )}
      </div>

      <div className="panel overflow-hidden fade-up" data-testid="calls-table-panel">
        {calls.isLoading ? (
          <Loading />
        ) : calls.data?.items?.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1f293d] hover:bg-transparent">
                  {["When", "Agent", "", "Client", "Duration", "Topic", "Sentiment", "Score", "Feedback"].map((h, i) => (
                    <TableHead key={i} className="text-[11px] font-mono uppercase tracking-wider text-slate-500 h-9">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.data.items.map((c) => (
                  <TableRow key={c.id} data-testid={`call-row-${c.id}`} className="border-[#1f293d] cursor-pointer row-hover" onClick={() => setParams({ call: c.id })}>
                    <TableCell className="font-mono text-xs text-slate-400 whitespace-nowrap">{fmtShort(c.created_at)}</TableCell>
                    <TableCell className="text-sm text-slate-100 whitespace-nowrap">{c.agent_name || "—"}</TableCell>
                    <TableCell className="w-8">{c.direction === "incoming" ? <PhoneIncoming className="h-3.5 w-3.5 text-cyan-400" /> : <PhoneOutgoing className="h-3.5 w-3.5 text-blue-400" />}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-300 whitespace-nowrap">{fmtPhone(c.client_number)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-300">
                      <span className="inline-flex items-center gap-1.5">
                        {c.duration ? fmtDuration(c.duration) : <span className="text-slate-600">{c.status}</span>}
                        {c.recording_status === "missing" && <span title="Not recorded" data-testid={`call-unrecorded-${c.id}`}><MicOff className="h-3 w-3 text-red-400" /></span>}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-300 max-w-[260px] truncate">
                      {c.topic ? (
                        <span className="flex items-center gap-1.5"><span className="truncate">{c.topic}</span>{c.call_type && <Pill>{c.call_type}</Pill>}</span>
                      ) : (
                        <span className="text-slate-600">{c.transcript_status === "absent" ? "No transcript available" : c.analysis_status === "failed" ? "Analysis failed" : c.transcript_status === "completed" ? "Transcript ready · queued" : c.duration >= 20 ? "Awaiting transcript" : "Too short to review"}</span>
                      )}
                    </TableCell>
                    <TableCell>{c.sentiment ? <Pill tone={sentimentTone(c.sentiment)}>{c.sentiment}</Pill> : <span className="text-slate-700">—</span>}</TableCell>
                    <TableCell><ScoreBadge score={c.score} size="sm" /></TableCell>
                    <TableCell>
                      {c.feedback_email?.status === "sent" ? (
                        <Pill tone={c.feedback_email.cc_applied ? "amber" : "blue"}><Mail className="h-3 w-3" /> {c.feedback_email.cc_applied ? "sent + CC" : "sent"}</Pill>
                      ) : c.feedback_email?.status === "failed" ? (
                        <Pill tone="red">failed</Pill>
                      ) : (
                        <span className="text-slate-700">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState title="No calls match" hint="Adjust filters or the date range. New calls arrive via the 5-minute OpenPhone sync and webhooks." testId="calls-empty" />
        )}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#1f293d] text-xs text-slate-500 font-mono">
          <span data-testid="calls-total">{calls.data?.total ?? 0} calls</span>
          <div className="flex items-center gap-1">
            <Button data-testid="calls-prev-page" variant="ghost" size="sm" className="h-7 px-2" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span>page {page} / {pages}</span>
            <Button data-testid="calls-next-page" variant="ghost" size="sm" className="h-7 px-2" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <CallDrawer callId={openId} onClose={() => setParams({})} />
    </>
  );
}
